import { Link } from "react-router-dom";
import {
  Leaf, Target, Eye, Users, Recycle, Network, TrendingUp, Package,
  ShoppingCart, Globe, ArrowRight,
} from "lucide-react";
import { GALLERY } from "../data/site";
import { Reveal } from "../components/Reveal";
import { usePageMeta } from "../hooks/usePageMeta";

const OBJECTIVES = [
  { icon: Recycle, title: "Replace Single-Use Plastic", desc: "Make sustainable alternatives the easy, everyday choice for businesses and communities." },
  { icon: Network, title: "Expand Our Dealer Network", desc: "Grow a district-wise dealer and distributor network across India." },
  { icon: Users, title: "Support Livelihoods", desc: "Create employment and income opportunities for tribal women." },
  { icon: TrendingUp, title: "Build a Trusted Eco Brand", desc: "Become a trusted, affordable eco-product brand across the country." },
];

const VISION_GOALS = [
  { icon: Network, title: "District-Wise Dealer Network", desc: "Expand our dealer and distribution network district by district across India." },
  { icon: Package, title: "Wider Product Range", desc: "Grow the range with different sizes and sustainable packaging options." },
  { icon: ShoppingCart, title: "Marketplace & B2B Growth", desc: "Grow through Amazon, Flipkart and bulk B2B partnerships." },
  { icon: Globe, title: "Leading Eco-Product Brand", desc: "Our long-term ambition: become a leading eco-product brand in India." },
];

export default function About() {
  usePageMeta(
    "About Us | ORGANIKTHINGS LLP",
    "Learn about ORGANIKTHINGS LLP — established 2021 in Indore, making natural, biodegradable alternatives to single-use plastic and supporting tribal women's livelihoods."
  );
  return (
    <main data-testid="about-page">
      {/* Intro */}
      <section className="bg-[#f4f1ea] py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs font-semibold tracking-wider uppercase text-[#2d6a4f] mb-3">About Us</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-[#0d1f18] leading-tight">
              Sustainable alternatives to single-use plastic, since 2021
            </h1>
            <p className="mt-5 text-base md:text-lg text-[#2d3748] leading-relaxed">
              ORGANIKTHINGS LLP focuses on sustainable and eco-friendly
              alternatives to single-use plastic. Established in 2021 and based
              in Indore, Madhya Pradesh, we make products from natural materials
              under the Organik Thing brand — leaf plates, bowls, thalis and
              grass straws supplied to businesses across India.
            </p>
          </div>
          <Reveal>
            <img
              src={GALLERY.hero}
              alt="Handcrafted natural leaf plates by Organik Things"
              className="w-full h-72 sm:h-96 object-cover rounded-2xl shadow-lg"
            />
          </Reveal>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="bg-white py-16 md:py-20" data-testid="about-vision-mission">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Reveal>
            <div className="bg-[#faf9f6] rounded-xl border border-[#e2ded4] p-8 h-full">
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-[#d8f3dc] text-[#1b4332]">
                <Eye className="w-5 h-5" />
              </span>
              <h2 className="mt-4 font-heading text-2xl font-bold text-[#0d1f18]">Our Vision</h2>
              <p className="mt-3 text-base text-[#5a6b5c] leading-relaxed">
                A greener tomorrow where natural, biodegradable products replace
                single-use plastic — and where ORGANIKTHINGS LLP grows into a
                leading eco-product brand in India.
              </p>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="bg-[#faf9f6] rounded-xl border border-[#e2ded4] p-8 h-full">
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-[#d8f3dc] text-[#1b4332]">
                <Target className="w-5 h-5" />
              </span>
              <h2 className="mt-4 font-heading text-2xl font-bold text-[#0d1f18]">Our Mission</h2>
              <p className="mt-3 text-base text-[#5a6b5c] leading-relaxed">
                To make sustainable products accessible and affordable for
                businesses and communities across India — through natural
                materials, thoughtful design and a growing dealer network.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* What we stand for */}
      <section className="bg-[#faf9f6] py-16 md:py-20" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <img
              src={GALLERY.thaliSpread}
              alt="Leaf thalis and bowls served with Indian meals"
              className="w-full h-64 sm:h-80 object-cover rounded-2xl"
              loading="lazy"
            />
          </Reveal>
          <div>
            <p className="text-xs font-semibold tracking-wider uppercase text-[#2d6a4f] mb-3">What We Stand For</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#0d1f18]">
              Natural products, honest work
            </h2>
            <ul className="mt-6 space-y-4">
              <li className="flex items-start gap-3">
                <Leaf className="w-5 h-5 text-[#2d6a4f] mt-0.5 shrink-0" />
                <p className="text-sm sm:text-base text-[#2d3748] leading-relaxed">
                  <strong className="text-[#0d1f18]">Natural & biodegradable alternatives.</strong>{" "}
                  Our products are made from natural materials, with plastic-free
                  design and dual-layer construction using biodegradable
                  cardboard and glue.
                </p>
              </li>
              <li className="flex items-start gap-3">
                <Users className="w-5 h-5 text-[#2d6a4f] mt-0.5 shrink-0" />
                <p className="text-sm sm:text-base text-[#2d3748] leading-relaxed">
                  <strong className="text-[#0d1f18]">People first.</strong>{" "}
                  We are committed to supporting tribal women's livelihoods and
                  keeping traditional craftsmanship alive through our work.
                </p>
              </li>
              <li className="flex items-start gap-3">
                <Recycle className="w-5 h-5 text-[#2d6a4f] mt-0.5 shrink-0" />
                <p className="text-sm sm:text-base text-[#2d3748] leading-relaxed">
                  <strong className="text-[#0d1f18]">Practical sustainability.</strong>{" "}
                  Affordable alternatives that real businesses can adopt — backed
                  by our business-reported milestone of 800,000+ pieces sold so far.
                </p>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Objectives */}
      <section className="bg-white py-16 md:py-20" data-testid="about-objectives">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs font-semibold tracking-wider uppercase text-[#2d6a4f] mb-3">Our Objectives</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#0d1f18]">
              What we're working towards
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {OBJECTIVES.map((o, i) => (
              <Reveal key={o.title} delay={i * 60}>
                <div className="bg-[#faf9f6] rounded-xl border border-[#e2ded4] p-6 h-full">
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-[#d8f3dc] text-[#1b4332]">
                    <o.icon className="w-5 h-5" />
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-[#0d1f18]">{o.title}</h3>
                  <p className="mt-2 text-sm text-[#5a6b5c] leading-relaxed">{o.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Growth vision */}
      <section className="bg-[#1b4332] py-16 md:py-20 text-white" data-testid="about-growth-vision">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs font-semibold tracking-wider uppercase text-[#95d5b2] mb-3">Our Growth Vision</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
              Where we're headed
            </h2>
            <p className="mt-4 text-base text-white/75 leading-relaxed max-w-2xl">
              The roadmap outlined in our business proposal — our goals and
              plans for the years ahead.
            </p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VISION_GOALS.map((v, i) => (
              <Reveal key={v.title} delay={i * 60}>
                <div className="bg-white/5 border border-white/10 rounded-xl p-6 h-full">
                  <v.icon className="w-6 h-6 text-[#95d5b2]" />
                  <h3 className="mt-3 font-heading font-bold text-base">{v.title}</h3>
                  <p className="mt-1.5 text-sm text-white/75 leading-relaxed">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <Link
              to="/dealership"
              data-testid="about-cta-dealership"
              className="mt-10 inline-flex items-center gap-2 bg-white text-[#1b4332] hover:bg-[#d8f3dc] font-semibold px-7 py-3.5 rounded-lg transition-colors"
            >
              Partner With Us <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
